// 会话管理工具
// 用于检测登录超时和自动退出
import { shouldAutoLogout } from './pageAuth';

class SessionManager {
  constructor() {
    this.sessionTimeout = 30 * 60 * 1000; // 30分钟无操作自动退出
    this.warningTime = 5 * 60 * 1000; // 提前5分钟警告
    this.lastActivity = null;
    this.warningShown = false;
    this.timeoutTimer = null;
    this.warningTimer = null;
  }

  // 初始化会话管理
  init() {
    // 监听用户活动
    this.bindActivityEvents();
    
    // 检查现有会话
    this.checkExistingSession();
    
    // 启动超时检测
    this.startTimeoutDetection();
    
    console.log('会话管理已初始化，超时时间:', this.sessionTimeout / 1000 / 60, '分钟');
  }

  // 绑定用户活动事件
  bindActivityEvents() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const updateActivity = () => {
      this.lastActivity = Date.now();
      localStorage.setItem('lastActivity', this.lastActivity.toString());
      this.warningShown = false;
      
      // 重新启动超时检测
      this.startTimeoutDetection();
    };

    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });
  }

  // 检查现有会话
  checkExistingSession() {
    const lastActivityStr = localStorage.getItem('lastActivity');
    if (lastActivityStr) {
      this.lastActivity = parseInt(lastActivityStr);
      const timeSinceActivity = Date.now() - this.lastActivity;
      
      if (timeSinceActivity > this.sessionTimeout) {
        console.log('检测到会话已超时，自动退出');
        this.handleSessionTimeout();
      } else {
        console.log('会话有效，剩余时间:', (this.sessionTimeout - timeSinceActivity) / 1000 / 60, '分钟');
      }
    } else {
      this.lastActivity = Date.now();
      localStorage.setItem('lastActivity', this.lastActivity.toString());
    }
  }

  // 启动超时检测
  startTimeoutDetection() {
    // 清除现有定时器
    this.clearTimers();
    
    if (!this.lastActivity) {
      this.lastActivity = Date.now();
    }

    const timeUntilTimeout = this.sessionTimeout - (Date.now() - this.lastActivity);
    const timeUntilWarning = timeUntilTimeout - this.warningTime;

    // 设置警告定时器
    if (timeUntilWarning > 0) {
      this.warningTimer = setTimeout(() => {
        this.showTimeoutWarning();
      }, timeUntilWarning);
    }

    // 设置超时定时器
    if (timeUntilTimeout > 0) {
      this.timeoutTimer = setTimeout(() => {
        this.handleSessionTimeout();
      }, timeUntilTimeout);
    } else {
      // 已经超时
      this.handleSessionTimeout();
    }
  }

  // 显示超时警告
  showTimeoutWarning() {
    if (this.warningShown) return;
    
    this.warningShown = true;
    const remainingMinutes = Math.ceil(this.warningTime / 1000 / 60);
    
    console.log(`会话将在 ${remainingMinutes} 分钟后过期`);
    
    // 显示警告提示
    if (window.confirm(`您的会话将在 ${remainingMinutes} 分钟后过期，是否继续使用？`)) {
      // 用户选择继续，更新活动时间
      this.lastActivity = Date.now();
      localStorage.setItem('lastActivity', this.lastActivity.toString());
      this.warningShown = false;
      this.startTimeoutDetection();
    } else {
      // 用户选择不继续，立即退出
      this.handleSessionTimeout();
    }
  }

  // 处理会话超时
  handleSessionTimeout() {
    console.log('会话超时，检查当前页面是否需要自动退出');
    
    if (!shouldAutoLogout()) {
      console.log('当前页面不需要自动退出，只清除定时器');
      // 只清除定时器，不清除登录状态
      this.clearTimers();
      return;
    }
    
    console.log('会话超时，执行自动退出');
    
    // 清除定时器
    this.clearTimers();
    
    // 停止心跳
    if (window.heartbeatManager) {
      window.heartbeatManager.stop();
    }
    
    // 清除所有登录相关的本地存储
    localStorage.removeItem('token');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('avatar');
    localStorage.removeItem('accountNickName');
    localStorage.removeItem('hasEditAllPermission');
    localStorage.removeItem('lastActivity');
    
    // 清除微信相关数据
    localStorage.removeItem('wechatUser');
    localStorage.removeItem('wechatToken');
    localStorage.removeItem('isWechatLoggedIn');
    
    // 设置手动退出标识，防止自动授权
    localStorage.setItem('manualLogout', 'true');
    
    // 显示超时提示
    // 直接跳转到登录页，不显示alert弹窗
    window.location.href = '/login';
  }

  // 清除定时器
  clearTimers() {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  // 手动延长会话
  extendSession() {
    this.lastActivity = Date.now();
    localStorage.setItem('lastActivity', this.lastActivity.toString());
    this.warningShown = false;
    this.startTimeoutDetection();
    console.log('会话已延长');
  }

  // 获取会话状态
  getSessionStatus() {
    if (!this.lastActivity) {
      return { isValid: false, remainingTime: 0 };
    }
    
    const remainingTime = this.sessionTimeout - (Date.now() - this.lastActivity);
    return {
      isValid: remainingTime > 0,
      remainingTime: Math.max(0, remainingTime),
      remainingMinutes: Math.ceil(remainingTime / 1000 / 60)
    };
  }

  // 销毁会话管理
  destroy() {
    this.clearTimers();
    
    // 移除事件监听器
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.removeEventListener(event, this.updateActivity, true);
    });
  }
}

// 创建全局会话管理器实例
const sessionManager = new SessionManager();

// 导出便捷方法
export const initSessionManager = () => sessionManager.init();
export const extendSession = () => sessionManager.extendSession();
export const getSessionStatus = () => sessionManager.getSessionStatus();
export const destroySessionManager = () => sessionManager.destroy();

export default sessionManager;
